import { createFileRoute, Link, Outlet, useChildMatches } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import {
  listAssessmentTemplates,
  createAssessmentTemplate,
  type AssessmentTemplateDto,
} from '../../lib/assessmentTemplates';
import { assignSession } from '../../lib/assessmentSessions';
import { listUsers, type UserDto } from '../../lib/users';
import { FileText, Plus, ChevronRight, Layers, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const Route = createFileRoute('/kinerja/templates')({
  component: TemplatesPage,
});

function TemplatesPage() {
  const childMatches = useChildMatches();
  if (childMatches.length > 0) {
    return (
      <ProtectedRoute minimumRole="hr">
        <Outlet />
      </ProtectedRoute>
    );
  }
  return (
    <ProtectedRoute minimumRole="hr">
      <TemplatesContent />
    </ProtectedRoute>
  );
}

function TemplatesContent() {
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;

  const [templates, setTemplates] = useState<AssessmentTemplateDto[]>([]);
  const [loading, setLoading] = useState(true);

  // Create dialog state
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Assign session dialog state
  const [assignTarget, setAssignTarget] = useState<AssessmentTemplateDto | null>(null);
  const [employees, setEmployees] = useState<UserDto[]>([]);
  const [employeesLoaded, setEmployeesLoaded] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [assignSuccess, setAssignSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    listAssessmentTemplates(companyId, true).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res.success && res.data) setTemplates(res.data.templates);
    });
    return () => { cancelled = true; };
  }, [companyId]);

  // Load employees lazily when assign dialog first opens
  useEffect(() => {
    if (!assignTarget || employeesLoaded) return;
    let cancelled = false;
    listUsers(companyId, {}).then((res) => {
      if (cancelled) return;
      setEmployeesLoaded(true);
      if (res.success && res.data) setEmployees(res.data.users);
    });
    return () => { cancelled = true; };
  }, [assignTarget, employeesLoaded, companyId]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setCreateError('');
    const res = await createAssessmentTemplate({
      companyId,
      name: newName,
      description: newDesc || undefined,
    });
    setCreating(false);
    if (res.success && res.data) {
      toast.success('Template created');
      setShowCreate(false);
      setNewName('');
      setNewDesc('');
      // Reload to get the full list with newly created template
      listAssessmentTemplates(companyId, true).then((r) => {
        if (r.success && r.data) setTemplates(r.data.templates);
      });
    } else {
      toast.error(res.error ?? 'Failed to create template');
      setCreateError(res.error ?? 'Failed to create template');
    }
  }

  function openAssign(template: AssessmentTemplateDto, e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setAssignTarget(template);
    setSelectedUserId('');
    setDueDate('');
    setAssignError('');
    setAssignSuccess('');
  }

  async function handleAssign(e: React.FormEvent) {
    e.preventDefault();
    if (!assignTarget || !selectedUserId) return;
    setAssigning(true);
    setAssignError('');
    setAssignSuccess('');
    const res = await assignSession(
      {
        companyId,
        templateId: assignTarget.id,
        assignedToUserId: selectedUserId,
        assignedByUserId: user?.id,
        dueDate: dueDate || undefined,
      },
    );
    setAssigning(false);
    if (res.success) {
      const emp = employees.find((u) => u.id === selectedUserId);
      toast.success(`Session assigned to ${emp?.name ?? 'employee'}`);
      setAssignSuccess(`Session assigned to ${emp?.name ?? 'employee'} successfully.`);
      setSelectedUserId('');
      setDueDate('');
    } else {
      toast.error(res.error ?? 'Failed to assign session');
      setAssignError(res.error ?? 'Failed to assign session');
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Assessment Templates</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Create and manage reusable assessment frameworks for your organisation
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4 mr-1.5" />
          New Template
        </Button>
      </div>

      {/* Template list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-16 text-center">
            <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center mb-4">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <p className="text-sm font-medium text-foreground">No templates yet</p>
            <p className="text-xs text-muted-foreground mt-1 mb-4">
              Create your first assessment template to get started
            </p>
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="w-4 h-4 mr-1.5" />
              New Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {templates.map((t) => (
            <Link
              key={t.id}
              to={'/kinerja/templates/$templateId' as any}
              params={{ templateId: t.id } as any}
            >
              <Card className="hover:shadow-md transition-shadow cursor-pointer group">
                <CardContent className="flex items-center gap-4 py-4">
                  <div className="w-9 h-9 rounded-lg bg-accent flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
                        {t.name}
                      </p>
                      <Badge variant={t.isActive ? 'success' : 'secondary'} className="shrink-0">
                        {t.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    {t.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{t.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Layers className="w-3 h-3" />
                        {t.sections.length} section{t.sections.length !== 1 ? 's' : ''}
                      </span>
                      <span>v{t.version}</span>
                    </div>
                  </div>
                  {/* Assign button — stops propagation so the link doesn't fire */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    onClick={(e) => openAssign(t, e)}
                  >
                    <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                    Assign
                  </Button>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New Assessment Template</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="tpl-name">Template name</Label>
              <Input
                id="tpl-name"
                required
                placeholder="e.g. Annual Performance Review"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tpl-desc">Description (optional)</Label>
              <Textarea
                id="tpl-desc"
                rows={2}
                placeholder="Brief description of what this template is for"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
              />
            </div>
            {createError && <p className="text-xs text-destructive">{createError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? 'Creating…' : 'Create Template'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign session dialog */}
      <Dialog open={!!assignTarget} onOpenChange={(open) => { if (!open) setAssignTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Assessment Session</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">
            Template: <span className="font-medium text-foreground">{assignTarget?.name}</span>
          </p>
          <form onSubmit={handleAssign} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label>Employee</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder={employeesLoaded ? 'Select employee…' : 'Loading…'} />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} — {emp.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="due-date">Due date (optional)</Label>
              <Input
                id="due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
            {assignError && <p className="text-xs text-destructive">{assignError}</p>}
            {assignSuccess && <p className="text-xs text-emerald-600">{assignSuccess}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAssignTarget(null)}>
                Close
              </Button>
              <Button type="submit" disabled={assigning || !selectedUserId}>
                {assigning ? 'Assigning…' : 'Assign Session'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
